import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogContent, Link,
  Paper, Stack, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';

/**
 * EVIDENCE, ON THE RECORD ITSELF.
 *
 * The photograph a wine was drafted from and the producer's own technical
 * sheet were reachable only from the scan; a curator looking at the wine
 * name could not see the bottle. This panel is READ-ONLY — curation stays
 * in the operator console — and it is deliberately quiet when a record has
 * no evidence, which most older records do not.
 */

interface EvidencePhoto {
  scanId: string; venueId: string; side: string; url: string | null;
  sha256: string | null; at: string; readVintage: number | null;
  verdict: string | null; via: string;
}
interface EvidenceSheet {
  techSheetId: string; sourceUrl: string; sourceTier: string;
  statedVintage: number | null; vintageMatch: boolean;
  url: string | null; statement: string;
}
interface EvidenceResponse {
  photos: EvidencePhoto[]; sheets: EvidenceSheet[]; scanIds: string[];
  wineNameId?: string; vintage?: number | null;
}

const TIER_LABEL: Record<string, string> = {
  'tech-sheet': 'producer’s sheet', 'producer-site': 'producer’s site',
  trade: 'trade', merchant: 'merchant',
};

export const EvidencePanel: React.FC<{
  /** 'wine-name' shows the whole cuvée; 'wine' shows only this vintage. */
  kind: 'wine-name' | 'wine';
  id: string;
}> = ({ kind, id }) => {
  const [data, setData] = useState<EvidenceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/${kind}/${id}/evidence`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind, id]);

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={20} /></Box>;
  const photos = data?.photos ?? [];
  const sheets = data?.sheets ?? [];
  // Quiet when there is nothing: an empty "Evidence" heading would imply a
  // gap where there is simply no scan.
  if (photos.length === 0 && sheets.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1">Evidence</Typography>
        <Typography variant="caption" color="text.secondary">
          {kind === 'wine'
            ? `what speaks to the ${data?.vintage ?? 'this'} vintage`
            : 'everything scanned for this wine, any vintage'}
        </Typography>
      </Stack>

      {photos.length > 0 && (
        <Box sx={{ mb: sheets.length ? 2 : 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Label photograph{photos.length === 1 ? '' : 's'}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {photos.map((p, i) => (
              <Box key={i} sx={{ width: 160 }}>
                {p.url ? (
                  <Box
                    component="img" src={p.url} alt={`${p.side} label`}
                    onClick={() => setZoom(p.url)}
                    sx={{ width: '100%', borderRadius: 1, cursor: 'zoom-in', border: '1px solid', borderColor: 'divider' }}
                  />
                ) : (
                  <Box sx={{ height: 100, bgcolor: 'action.hover', borderRadius: 1 }} />
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  {p.side}{p.readVintage ? ` · read ${p.readVintage}` : ''} · via {p.via}
                </Typography>
                <Link component={RouterLink} to={`/admin/governance/label-scans/${p.scanId}`} variant="caption">
                  the scan
                </Link>
                {p.sha256 && (
                  <Typography variant="caption" color="text.secondary" display="block"
                    sx={{ fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all' }}>
                    {p.sha256.slice(0, 16)}…
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {sheets.length > 0 && (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Technical sheet{sheets.length === 1 ? '' : 's'}
          </Typography>
          <Stack spacing={1}>
            {sheets.map((s) => (
              <Alert key={s.techSheetId} severity={s.vintageMatch ? 'success' : 'info'}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2">{s.statement}</Typography>
                  <Chip
                    size="small"
                    label={TIER_LABEL[s.sourceTier] ?? s.sourceTier}
                    color={s.sourceTier === 'tech-sheet' ? 'success' : 'default'}
                    variant={s.sourceTier === 'tech-sheet' ? 'filled' : 'outlined'}
                  />
                  {s.url && (
                    <Link href={s.url} target="_blank" rel="noopener noreferrer" variant="body2">
                      View the sheet
                    </Link>
                  )}
                </Stack>
              </Alert>
            ))}
          </Stack>
        </Box>
      )}

      {/* Click to zoom, as the console does it. */}
      <Dialog open={Boolean(zoom)} onClose={() => setZoom(null)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          {zoom && (
            <Box component="img" src={zoom} alt="label, full size"
              sx={{ maxWidth: '100%', display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>

      {kind === 'wine' && data?.wineNameId && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          <Link component={RouterLink} to={`/admin/wine-names/edit/${data.wineNameId}`}>
            See all evidence for this wine, any vintage
          </Link>
        </Typography>
      )}
    </Paper>
  );
};

export default EvidencePanel;
